import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material'
import { Controller, FieldValues } from 'react-hook-form'
import type { Control, UseFormClearErrors } from 'react-hook-form'
import { DateFieldInputRow } from '../ScheduleFields.style'
import { fieldCaptionLabelSx } from '../ScheduleFields.utils'
import type {
  ScheduleFieldNames,
  ScheduleFieldsProps,
} from '../ScheduleFields.utils'

export type TimeZoneSelectProps<T extends FieldValues> = {
  control: Control<T>
  fieldNames: ScheduleFieldNames
  timeZoneControl: NonNullable<
    ScheduleFieldsProps<T>['embeddedTimeZoneControl']
  >
  disabled: boolean
  shouldShowErrors: boolean
  clearErrors: UseFormClearErrors<T>
  getFieldError: (fieldKey: string) => string | undefined
}

export function TimeZoneSelect<T extends FieldValues>({
  control,
  fieldNames,
  timeZoneControl,
  disabled,
  shouldShowErrors,
  clearErrors,
  getFieldError,
}: TimeZoneSelectProps<T>) {
  if (!fieldNames.timeZone) return null

  const { timeZoneLabel, options, currentTimeZone } = timeZoneControl
  const selectId = `${String(fieldNames.timeZone)}-select`
  const timeZoneError = getFieldError(fieldNames.timeZone)

  return (
    <DateFieldInputRow item container>
      <Grid item xs={12} md={9}>
        <InputLabel shrink htmlFor={selectId} sx={fieldCaptionLabelSx}>
          {timeZoneLabel}
        </InputLabel>
        <FormControl
          fullWidth
          variant="outlined"
          error={Boolean(timeZoneError)}
        >
          <Controller
            name={fieldNames.timeZone as any}
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                id={selectId}
                displayEmpty
                value={field.value || currentTimeZone}
                onChange={event => {
                  field.onChange(event)
                  if (shouldShowErrors) clearErrors(fieldNames.timeZone as any)
                }}
                disabled={disabled}
              >
                {Array.from(new Set([currentTimeZone, ...options])).map(
                  option => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  )
                )}
              </Select>
            )}
          />
        </FormControl>
        {timeZoneError ? (
          <FormHelperText error sx={{ mx: 0 }}>
            {timeZoneError}
          </FormHelperText>
        ) : null}
      </Grid>
    </DateFieldInputRow>
  )
}
